import { useState } from 'react';
import { Loader2, ShieldCheck } from 'lucide-react';
import { useAuthStore, API_BASE, completeStepUp, cancelStepUp } from '../../lib/auth/AuthContext';

export function StepUpModal() {
  const stepUpRequired = useAuthStore(s => s.stepUpRequired);
  const token = useAuthStore(s => s.token);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (!stepUpRequired) return null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/auth/mfa/step-up`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ code }),
      });
      const data = await res.json() as { ok: boolean; data?: { access_token: string }; error?: { message: string } };
      if (!data.ok) throw new Error(data.error?.message || 'Verification failed');
      if (data.data?.access_token) {
        useAuthStore.setState(s => ({ ...s, token: data.data!.access_token }));
      }
      setCode('');
      completeStepUp();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24,
      backdropFilter: 'blur(4px)',
    }}>
      <div style={{
        background: 'var(--panel-dark)',
        borderRadius: 14,
        padding: '36px 32px',
        width: '100%',
        maxWidth: 380,
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, textAlign: 'center' }}>
          <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(222,56,49,0.1)', display: 'grid', placeItems: 'center' }}>
            <ShieldCheck size={22} style={{ color: 'var(--accent)' }} />
          </div>
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 400, letterSpacing: '-0.005em', color: '#16211b', margin: 0 }}>
              Verify your identity
            </h2>
            <p style={{ color: '#4d5a52', fontSize: 14, marginTop: 6, fontWeight: 400 }}>
              This action requires your authenticator code
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={{ display: 'block', fontSize: 11, color: '#4d5a52', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>
              Authentication Code
            </label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
              placeholder="000000"
              autoFocus
              required
              style={{
                width: '100%',
                padding: '12px 16px',
                borderRadius: 14,
                border: '1px solid rgba(0,0,0,0.06)',
                background: 'rgba(0,0,0,0.03)',
                color: '#1f2a24',
                fontSize: 18,
                letterSpacing: '0.3em',
                textAlign: 'center',
                fontFamily: "'JetBrains Mono', monospace",
                outline: 'none',
              }}
            />
          </div>

          {error && <p style={{ color: 'var(--accent)', fontSize: 13, margin: 0 }}>{error}</p>}

          <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
            <button
              type="submit"
              disabled={loading || code.length !== 6}
              style={{
                flex: 1,
                padding: '12px 20px',
                borderRadius: 8,
                border: 'none',
                background: 'var(--accent)',
                color: '#16211b',
                fontSize: 15,
                fontWeight: 400,
                cursor: 'pointer',
                fontFamily: "'Inter Tight', system-ui, sans-serif",
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                opacity: (loading || code.length !== 6) ? 0.6 : 1,
              }}
            >
              {loading ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : null}
              Verify
            </button>
            <button
              type="button"
              onClick={cancelStepUp}
              style={{
                padding: '12px 20px',
                borderRadius: 8,
                border: '1px solid rgba(0,0,0,0.07)',
                background: 'transparent',
                color: '#4d5a52',
                fontSize: 15,
                cursor: 'pointer',
                fontFamily: "'Inter Tight', system-ui, sans-serif",
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
